import React, { useCallback, useEffect, useMemo, useState } from "react";
import BreadCrumb from "Common/BreadCrumb";
import Select from "react-select";
import { Loader } from "../../Common/Loader";
// Icons
import { Calendar, Search, Users, BookOpen, ChevronLeft, ChevronRight } from "lucide-react";
// API Helpers
import { getProgramList, getClassList, saveAttendance, getAttendance } from "helpers/backend_helper";
import { useProgramStore } from "store/useProgramStore";
import { useClassStore } from "store/useClassStore";
import { useAuthStore } from "store/useAuthStore";

// This will be replaced with API data

// Default options for dropdowns
const defaultProgramOption = { value: "", label: "All Programs" };
const defaultClassOption = { value: "", label: "All Classes" };

// Attendance status options with API mapping
const attendanceOptions = [
  { value: 1, apiValue: 1, label: 'Present', shortLabel: 'P', color: 'text-green-600', bgColor: 'bg-green-100' },
  { value: 2, apiValue: 2, label: 'Absent', shortLabel: 'A', color: 'text-red-600', bgColor: 'bg-red-100' },
  { value: 3, apiValue: 3, label: 'Holiday', shortLabel: 'H', color: 'text-blue-600', bgColor: 'bg-blue-100' },
  { value: 4, apiValue: 4, label: 'Late', shortLabel: 'L', color: 'text-yellow-600', bgColor: 'bg-yellow-100' }
];

function DailyAttendance() {

  const {
    programs,
    selectedProgram,
    setSelectedProgram,
    fetchPrograms
  } = useProgramStore();

  const {
    classes,
    selectedClass,
    setSelectedClass,
  } = useClassStore();

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [searchText, setSearchText] = useState('');
  // const [selectedProgram, setSelectedProgram] = useState<any>(defaultProgramOption);
  // const [selectedClass, setSelectedClass] = useState<any>(defaultClassOption);
  const [selectedDate, setSelectedDate] = useState<string>(() => {
    const now = new Date();
    return now.toISOString().split('T')[0]; // YYYY-MM-DD format
  });

  const { user } = useAuthStore();
  // API Data States

  // Use Zustand store data directly - convert to dropdown format
  const programOptions = useMemo(() => {
    const options = programs.map((program: any) => ({
      value: program.programId,
      label: program.programName
    }));
    return [defaultProgramOption, ...options];
  }, [programs]);

  const classOptions = useMemo(() => {
    const options = classes.map((classItem: any) => ({
      value: classItem.classId,
      label: classItem.className
    }));
    return [defaultClassOption, ...options];
  }, [classes]);

  // Convert selected values from Zustand store to dropdown format
  const selectedProgramOption = useMemo(() => {
    if (!selectedProgram) return defaultProgramOption;
    return {
      value: selectedProgram.programId,
      label: selectedProgram.programName
    };
  }, [selectedProgram]);

  const selectedClassOption = useMemo(() => {
    if (!selectedClass) return defaultClassOption;
    return {
      value: selectedClass.classId,
      label: selectedClass.className
    };
  }, [selectedClass]);

  // Attendance data from API
  const [attendanceRecords, setAttendanceRecords] = useState<any[]>([]);
  const [filteredStudents, setFilteredStudents] = useState<any[]>([]);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState<boolean>(true);

  // Attendance data - stores attendance status for each student
  const [attendanceData, setAttendanceData] = useState<{ [key: string]: { attendanceType: number | null, attendanceId: number } }>({});
  const [savingStudents, setSavingStudents] = useState<Set<string>>(new Set());

  // Stats calculations
  const totalStudents = filteredStudents.length;
  const presentCount = Object.values(attendanceData).filter(data => data.attendanceType === 1).length;
  const absentCount = Object.values(attendanceData).filter(data => data.attendanceType === 2).length;
  const lateCount = Object.values(attendanceData).filter(data => data.attendanceType === 4).length;

  const handleProgramChange = (option: any) => {
    // Option is already in the format {value: programId, label: programName}
    if (option && option.value && option.value !== "") {
      // Find the program from Zustand store and update it
      const program = programs.find(p => p.programId === option.value);
      if (program) {
        setSelectedProgram(program);
        console.log("Program selected:", program);
      }
    } else {
      setSelectedProgram(null);
      console.log("Program cleared");
    }
    // Clear attendance data when program changes
    setAttendanceData({});
  };

  const handleClassChange = (option: any) => {
    // Option is already in the format {value: classId, label: className}
    if (option && option.value && option.value !== "") {
      // Find the class from Zustand store and update it
      const classItem = classes.find(c => c.classId === option.value);
      if (classItem) {
        setSelectedClass(classItem);
        console.log("Class selected:", classItem);
      }
    } else {
      setSelectedClass(null);
      console.log("Class cleared");
    }
    // Clear attendance data when class changes
    setAttendanceData({});
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedDate(e.target.value);
    // Note: attendance data will be cleared by useEffect
  };

  const handlePrevDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() - 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
    // Note: attendance data will be cleared by useEffect
  };

  const handleNextDate = () => {
    const currentDate = new Date(selectedDate);
    currentDate.setDate(currentDate.getDate() + 1);
    setSelectedDate(currentDate.toISOString().split('T')[0]);
    // Note: attendance data will be cleared by useEffect
  };

  const handleAttendanceChange = async (studentId: string, attendanceType: number) => {
    // Update local state first for immediate UI feedback
    const currentData = attendanceData[studentId];
    const newAttendanceData = {
      attendanceType: attendanceType,
      attendanceId: currentData?.attendanceId || 0
    };

    setAttendanceData(prev => ({
      ...prev,
      [studentId]: newAttendanceData
    }));

    // Save to API immediately
    await saveAttendanceRecord(studentId, attendanceType, newAttendanceData.attendanceId);
  };

  // Save individual attendance record
  const saveAttendanceRecord = async (studentId: string, attendanceType: number, attendanceId: number) => {
    // Add student to saving set
    setSavingStudents(prev => new Set(Array.from(prev).concat(studentId)));

    try {
      const record = {
        clientId: 1,
        programId: selectedProgram?.programId,
        classId: selectedClass?.classId,
        studentId: parseInt(studentId),
        attendanceDay: selectedDate + "T16:45:16.419Z",
        attendanceType: attendanceType,
        notes: "",
        createdBy: user?.userId // Use actual user ID from zustand store
      };

      console.log("Saving attendance record:", record);

      if (attendanceId === 0) {
        // Add new attendance record
        const response = await saveAttendance(record) as any;
        console.log("Add response:", response);

        // Update the attendanceId in local state after successful creation
        if (response && response.attendanceId) {
          setAttendanceData(prev => ({
            ...prev,
            [studentId]: {
              attendanceType: attendanceType,
              attendanceId: response.attendanceId
            }
          }));
        }
      } else {
        // Update existing attendance record
        const updateRecord = {
          ...record,
          attendanceId: attendanceId
        };
        const response = await saveAttendance(updateRecord);
        console.log("Update response:", response);
      }

    } catch (error) {
      console.error("Error saving attendance record:", error);
      // Optionally show error message to user
      // alert("Error saving attendance. Please try again.");
    } finally {
      // Remove student from saving set
      setSavingStudents(prev => {
        const newSet = new Set(prev);
        newSet.delete(studentId);
        return newSet;
      });
    }
  };



  // Load Attendance from API
  const loadAttendance = async () => {
    if (!selectedProgram?.programId || !selectedClass?.classId) {
      setAttendanceRecords([]);
      setFilteredStudents([]);
      setAttendanceData({});
      setIsLoadingAttendance(false);
      return;
    }

    try {
      const response = await getAttendance({
        programId: selectedProgram?.programId,
        classId: selectedClass?.classId,
        attendanceDay: selectedDate
      });

      console.log("Attendance response:", response);

      if (response.data && Array.isArray(response.data)) {
        setAttendanceRecords(response.data);

        // Create students list from attendance data
        const students = response.data.map((record: any) => ({
          studentId: record.studentId,
          firstName: record.studentName.split(' ')[0] || record.studentName,
          lastName: record.studentName.split(' ').slice(1).join(' ') || '',
          fullName: record.studentName
        }));
        setFilteredStudents(students);

        // Set attendance data
        const attendanceMap: { [key: string]: { attendanceType: number | null, attendanceId: number } } = {};
        response.data.forEach((record: any) => {
          attendanceMap[record.studentId.toString()] = {
            attendanceType: record.attendanceType,
            attendanceId: record.attendanceId
          };
        });
        setAttendanceData(attendanceMap);
      } else {
        setAttendanceRecords([]);
        setFilteredStudents([]);
        setAttendanceData({});
      }
    } catch (error) {
      console.error("Error loading attendance:", error);
      setAttendanceRecords([]);
      setFilteredStudents([]);
      setAttendanceData({});
    } finally {
      setIsLoadingAttendance(false);
    }
  };

  useEffect(() => {
    // Load data from Zustand stores if not already loaded
    const loadData = async () => {
      console.log("Starting to load dropdown data...");
      
      
      // Ensure default selections are set if not already selected
      if (programs.length > 0 && !selectedProgram) {
        setSelectedProgram(programs[0]);
        console.log("Set default program:", programs[0]);
      }
      
      if (classes.length > 0 && !selectedClass) {
        setSelectedClass(classes[0]);
        console.log("Set default class:", classes[0]);
      }
      
      setIsLoading(false);
      console.log("Finished loading dropdown data");
    };

    loadData();
  }, [programs.length, classes.length, selectedProgram, selectedClass, setSelectedProgram, setSelectedClass]);

  useEffect(() => {
    // Load attendance when program, class, or date changes
    if (selectedProgram?.programId && selectedClass?.classId && selectedDate) {
      setIsLoadingAttendance(true);
      loadAttendance();
    } else {
      setFilteredStudents([]);
      setAttendanceData({});
      setIsLoadingAttendance(false);
    }
  }, [selectedProgram, selectedClass, selectedDate]);

  // Debug effect to log Zustand store changes
  useEffect(() => {
    console.log("Program store updated:", { programs, selectedProgram });
  }, [programs, selectedProgram]);

  useEffect(() => {
    console.log("Class store updated:", { classes, selectedClass });
  }, [classes, selectedClass]);

  // Ensure default selections when stores are loaded
  useEffect(() => {
    if (programs.length > 0 && !selectedProgram) {
      setSelectedProgram(programs[0]);
      console.log("Auto-selected first program:", programs[0]);
    }
  }, [programs, selectedProgram, setSelectedProgram]);

  useEffect(() => {
    if (classes.length > 0 && !selectedClass) {
      setSelectedClass(classes[0]);
      console.log("Auto-selected first class:", classes[0]);
    }
  }, [classes, selectedClass, setSelectedClass]);

  // Format date for display
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <React.Fragment>
      <div className="container-fluid group-data-[content=boxed]:max-w-boxed mx-auto">
        <div className="flex flex-col gap-2 py-4 md:py-6">

          {/* Stats Cards */}
          <div className="grid grid-cols-1 gap-x-5 md:grid-cols-2 xl:grid-cols-4">
            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-blue-100 rounded-md dark:bg-blue-500/20">
                      <Users className="size-6 text-blue-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{totalStudents}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Total Students</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-green-100 rounded-md dark:bg-green-500/20">
                      <Users className="size-6 text-green-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{presentCount}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Present</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-red-100 rounded-md dark:bg-red-500/20">
                      <Users className="size-6 text-red-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{absentCount}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Absent</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-body">
                <div className="flex items-center">
                  <div className="shrink-0">
                    <div className="flex items-center justify-center size-12 bg-yellow-100 rounded-md dark:bg-yellow-500/20">
                      <Users className="size-6 text-yellow-500" />
                    </div>
                  </div>
                  <div className="grow ms-5">
                    <h5 className="mb-1 text-slate-700 dark:text-zink-200">{lateCount}</h5>
                    <p className="text-slate-500 dark:text-zink-400">Late</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content Card */}
          <div className="card">
            <div className="card-body">

              {/* Filters Section */}
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-12 xl:grid-cols-12 mb-5">

                {/* Search */}
                <div className="lg:col-span-3">
                  <div className="relative">
                    <input
                      type="text"
                      className="ltr:pl-8 rtl:pr-8 search form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200"
                      placeholder="Search students..."
                      autoComplete="off"
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                    />
                    <Search className="inline-block size-4 absolute ltr:left-2.5 rtl:right-2.5 top-2.5 text-slate-500 dark:text-zink-200 fill-slate-100 dark:fill-zink-600" />
                  </div>
                </div>

                {/* Program Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Program"
                    name="program"
                    options={programOptions}
                    value={selectedProgramOption}
                    onChange={handleProgramChange}
                    isLoading={programs.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Class Filter */}
                <div className="lg:col-span-3">
                  <Select
                    className="basic-single"
                    classNamePrefix="select"
                    placeholder="Select Class"
                    name="class"
                    options={classOptions}
                    value={selectedClassOption}
                    onChange={handleClassChange}
                    isLoading={classes.length === 0}
                    theme={(theme) => ({
                      ...theme,
                      colors: {
                        ...theme.colors,
                        primary: '#3b71ca',
                      },
                    })}
                  />
                </div>

                {/* Date Navigation */}
                <div className="lg:col-span-3">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handlePrevDate}
                      className="flex items-center justify-center size-10 bg-white border border-slate-200 rounded-md hover:bg-slate-50 dark:bg-zink-700 dark:border-zink-500 dark:hover:bg-zink-600"
                    >
                      <ChevronLeft className="size-4" />
                    </button>
                    <div className="flex-1 relative">
                      <input
                        type="date"
                        className="form-input border-slate-200 dark:border-zink-500 focus:outline-none focus:border-custom-500 disabled:bg-slate-100 dark:disabled:bg-zink-600 disabled:border-slate-300 dark:disabled:border-zink-500 dark:disabled:text-zink-200 disabled:text-slate-500 dark:text-zink-100 dark:bg-zink-700 dark:focus:border-custom-800 placeholder:text-slate-400 dark:placeholder:text-zink-200
                        
                        "
                        value={selectedDate}
                        onChange={handleDateChange}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={handleNextDate}
                      className="flex items-center justify-center size-10 bg-white border border-slate-200 rounded-md hover:bg-slate-50 dark:bg-zink-700 dark:border-zink-500 dark:hover:bg-zink-600"
                    >
                      <ChevronRight className="size-4" />
                    </button>
                  </div>
                </div>


                {/* Date Display */}
                {/* <div className="lg:col-span-4">
                  <h5 className="text-slate-700 dark:text-zink-200 mt-1">
                    Attendance for {formatDate(selectedDate)}
                  </h5>
                </div> */}
              </div>

              {/* Students Attendance Grid */}
              {isLoading || isLoadingAttendance ? (
                <Loader />
              ) : !selectedProgram?.programId || !selectedClass?.classId ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <Calendar className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">Select Program and Class</p>
                    <p>Please select both a program and class to view and mark attendance.</p>
                  </div>
                </div>
              ) : filteredStudents.length === 0 ? (
                <div className="text-center py-8">
                  <div className="text-slate-500 dark:text-zink-400">
                    <Users className="size-12 mx-auto mb-4 opacity-50" />
                    <p className="text-lg font-medium mb-2">No Students Found</p>
                    <p>No students found for the selected program and class combination.</p>
                  </div>
                </div>
              ) : (
                <div className="w-full">
                  <div className="overflow-x-auto">
                    <table className="w-full whitespace-nowrap">
                    <thead className="ltr:text-left rtl:text-right bg-slate-100 dark:bg-zink-600">
                      <tr>
                        <th className="px-3 py-3 font-semibold border-b border-slate-200 dark:border-zink-500">
                          Student ID
                        </th>
                      
                        <th className="px-3 py-3 font-semibold border-b border-slate-200 dark:border-zink-500 text-center">
                          Attendance Status
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredStudents.map((student, index) => (
                        <tr key={student.studentId} className={`even:bg-slate-50 dark:odd:bg-zink-700 dark:even:bg-zink-600 ${savingStudents.has(student.studentId.toString()) ? 'opacity-60' : ''
                          }`}>
                          {/* <td className="px-3 py-3 border-y border-slate-200 dark:border-zink-500">
                            <div className="flex items-center gap-2">
                              <div className="font-medium text-slate-900 dark:text-zink-50">
                                {student.studentId || `STU${student.studentId}`}
                              </div>
                              {savingStudents.has(student.studentId.toString()) && (
                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-500"></div>
                              )}
                            </div>
                          </td> */}
                          <td className="px-3 py-3 border-y border-slate-200 dark:border-zink-500">
                            <div className="font-medium text-slate-900 dark:text-zink-50">
                              {student.fullName || `${student.firstName} ${student.lastName}`}
                            </div>
                          </td>
                          <td className="px-3 py-3 border-y border-slate-200 dark:border-zink-500">
                            <div className="flex justify-center gap-4">
                              {attendanceOptions.map((option) => (
                                <label key={option.value} className={`flex items-center gap-2 cursor-pointer ${savingStudents.has(student.studentId.toString()) ? 'pointer-events-none' : ''
                                  }`}>
                                  <input
                                    type="radio"
                                    name={`attendance-${student.studentId}`}
                                    value={option.value}
                                    checked={attendanceData[student.studentId]?.attendanceType === option.value}
                                    onChange={() => handleAttendanceChange(student.studentId.toString(), option.value)}
                                    className="hidden"
                                    disabled={savingStudents.has(student.studentId.toString())}
                                  />
                                  <div className={`flex items-center justify-center w-8 h-8 rounded-lg border transition-all ${attendanceData[student.studentId]?.attendanceType === option.value
                                      ? `${option.bgColor} border-current ${option.color}`
                                      : 'border-slate-300 hover:border-slate-400'
                                    }`}>
                                    <span className={`font-semibold text-sm ${attendanceData[student.studentId]?.attendanceType === option.value
                                        ? option.color
                                        : 'text-slate-400'
                                      }`}>
                                      {option.shortLabel}
                                    </span>
                                  </div>
                                  <span className={`text-sm hidden md:inline ${attendanceData[student.studentId]?.attendanceType === option.value
                                      ? option.color + ' font-medium'
                                      : 'text-slate-500'
                                    }`}>
                                    {option.label}
                                  </span>
                                </label>
                              ))}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  </div>
                </div>
              )}

            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default DailyAttendance;
